/**
 * iter332 — Katalog Portal (bağımsız modül).
 *
 * 3 Sekme:
 *   1) Katalog Listesi — hazırlanmış PDF kataloglar.
 *   2) Malzeme Kataloğu — manuel veya envanterden eklenen malzemeler.
 *   3) Ürün Kataloğu — Müşteri/Marka/Ürün/Tanıtım + görseller (FİYAT YOK).
 *
 * Katalog MODÜLDEN BAĞIMSIZDIR — hiçbir UI aksiyonu başka business
 * modüle WRITE yapmaz; sadece Envanter/Müşteri'yi READ eder.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { openPdfPreview } from "@/lib/pdfPreview";
import SmartSuggestInput from "@/components/SmartSuggestInput";
import { useDragReorder } from "@/lib/useDragReorder";
import {
  Package, Image as ImgIcon, Trash2, Edit3, Copy, Send, Mail, FileText,
  Plus, Upload, Star, StarOff, X, ChevronLeft, ChevronRight, GripVertical,
  Boxes, PlusCircle, Search, Download,
} from "lucide-react";

const CURRENCIES = ["EUR", "USD", "TRY"];

function cx(...xs) { return xs.filter(Boolean).join(" "); }
function fmtDate(iso) { return (iso || "").slice(0, 10); }
function thumbUrl(photoId) { return `${API}/catalog/photo/${photoId}?variant=thumb`; }
function fullUrl(photoId) { return `${API}/catalog/photo/${photoId}?variant=full`; }

// ───────────────────────────────────────────────────────────────
// Shared: Image Lightbox (ileri/geri)
// ───────────────────────────────────────────────────────────────
function Lightbox({ photos, startIdx, onClose }) {
  const [idx, setIdx] = useState(startIdx || 0);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIdx(i => (i + 1) % photos.length);
      if (e.key === "ArrowLeft") setIdx(i => (i - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [photos.length, onClose]);
  if (!photos || photos.length === 0) return null;
  const p = photos[idx];
  return (
    <div className="fixed inset-0 z-[80] bg-black/85 flex items-center justify-center" onClick={onClose}
         data-testid="catalog-lightbox">
      <button onClick={e => { e.stopPropagation(); onClose(); }}
              className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded">
        <X size={20} />
      </button>
      {photos.length > 1 && (
        <>
          <button onClick={e => { e.stopPropagation(); setIdx(i => (i - 1 + photos.length) % photos.length); }}
                  className="absolute left-6 p-2 text-white hover:bg-white/10 rounded" data-testid="lightbox-prev">
            <ChevronLeft size={28} />
          </button>
          <button onClick={e => { e.stopPropagation(); setIdx(i => (i + 1) % photos.length); }}
                  className="absolute right-6 p-2 text-white hover:bg-white/10 rounded" data-testid="lightbox-next">
            <ChevronRight size={28} />
          </button>
        </>
      )}
      <img src={fullUrl(p.id)} alt="" className="max-h-[90vh] max-w-[90vw] object-contain shadow-2xl"
           onClick={e => e.stopPropagation()} />
      <div className="absolute bottom-4 text-white text-xs opacity-80">
        {idx + 1} / {photos.length}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Shared: Photo Grid (3×3 cm thumbnails + drag/drop + primary)
// ───────────────────────────────────────────────────────────────
function PhotoManager({ kind, record, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const photos = record?.photos || [];
  const dnd = useDragReorder(() => photos, async (arr) => {
    const order = arr.map(p => p.id);
    try {
      await api.put(`/catalog/${kind}/${record.id}/photos/order`, { order });
      onChange?.();
    } catch (e) { alert("Sıralama kaydedilemedi."); }
  });
  const uploadFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        if (!f.type?.startsWith("image/")) continue;
        const fd = new FormData();
        fd.append("file", f);
        await api.postForm(`/catalog/${kind}/${record.id}/photos`, fd);
      }
      onChange?.();
    } catch (err) {
      alert("Yükleme hatası: " + (err?.response?.data?.detail || err.message));
    } finally { setUploading(false); }
  };
  const handleUpload = async (e) => {
    await uploadFiles(e.target.files);
    e.target.value = "";
  };
  const handleDrop = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer?.files || []);
    await uploadFiles(files);
  };
  const setPrimary = async (pid) => {
    await api.put(`/catalog/${kind}/${record.id}/photos/${pid}/primary`, {});
    onChange?.();
  };
  const deletePhoto = async (pid) => {
    if (!window.confirm("Fotoğrafı sil?")) return;
    await api.del(`/catalog/${kind}/${record.id}/photos/${pid}`);
    onChange?.();
  };
  return (
    <div data-testid={`photo-manager-${kind}-${record.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <label className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-indigo-200 hover:bg-indigo-50 text-indigo-700 cursor-pointer">
          <Upload size={13} />
          {uploading ? "Yükleniyor..." : "Fotoğraf Yükle"}
          <input type="file" accept="image/*" multiple onChange={handleUpload} className="hidden"
                 data-testid={`photo-upload-${kind}-${record.id}`} />
        </label>
        <span className="text-[10px] text-slate-400">
          {photos.length} görsel · sürükle-bırak ile sırala · yıldızlı = ana görsel
        </span>
      </div>
      <div
        onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
        onDragLeave={(e) => { if (e.currentTarget.contains(e.relatedTarget)) return; setIsDragging(false); }}
        onDrop={handleDrop}
        className={cx(
          "flex flex-wrap gap-2 p-2 rounded border-2 border-dashed transition",
          isDragging ? "border-indigo-500 bg-indigo-50" : "border-transparent",
        )}
        data-testid={`photo-dropzone-${kind}-${record.id}`}
      >
        {photos.length === 0 && (
          <div className="w-full text-center text-[11px] text-slate-400 py-6">
            Fotoğrafları buraya sürükleyip bırakın veya yukarıdaki butonu kullanın.
          </div>
        )}
        {photos.map((p, i) => (
          <div key={p.id} {...dnd.rowProps(i)}
               className={cx(
                 "relative w-[90px] h-[90px] rounded border-2 overflow-hidden group bg-slate-50",
                 p.is_primary ? "border-amber-400" : "border-slate-200",
                 dnd.isDropTarget(i) && "ring-2 ring-indigo-400",
                 dnd.isDragging(i) && "opacity-50",
               )}
               data-testid={`photo-tile-${p.id}`}>
            <img src={thumbUrl(p.id)} alt="" className="w-full h-full object-cover cursor-zoom-in"
                 onClick={() => setLightbox({ photos, idx: i })} />
            <div draggable {...dnd.handleProps(i)}
                 className="absolute top-0.5 left-0.5 p-0.5 bg-white/80 rounded cursor-move opacity-0 group-hover:opacity-100 transition">
              <GripVertical size={11} className="text-slate-600" />
            </div>
            <button onClick={() => setPrimary(p.id)} title={p.is_primary ? "Ana görsel" : "Ana görsel yap"}
                    className="absolute top-0.5 right-0.5 p-0.5 bg-white/85 rounded hover:bg-amber-100">
              {p.is_primary ? <Star size={11} className="text-amber-500 fill-amber-500" />
                            : <StarOff size={11} className="text-slate-400" />}
            </button>
            <button onClick={() => deletePhoto(p.id)} title="Sil"
                    className="absolute bottom-0.5 right-0.5 p-0.5 bg-white/85 rounded hover:bg-red-100 opacity-0 group-hover:opacity-100 transition">
              <Trash2 size={11} className="text-red-500" />
            </button>
          </div>
        ))}
      </div>
      {lightbox && (
        <Lightbox photos={lightbox.photos} startIdx={lightbox.idx} onClose={() => setLightbox(null)} />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Material Editor (manuel + envanterden ekleme)
// ───────────────────────────────────────────────────────────────
function MaterialEditor({ initial, categories, onClose, onSaved }) {
  const [m, setM] = useState(() => initial || {
    name: "", category: "", short_description: "", price: "",
    currency: "EUR", note: "",
  });
  const [mode, setMode] = useState(initial ? "edit" : "manual"); // manual | inventory | edit
  const [invSearch, setInvSearch] = useState("");
  const [invResults, setInvResults] = useState([]);
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    if (mode !== "inventory") return;
    const t = setTimeout(async () => {
      try { setInvResults(await api.get("/catalog/inventory-materials", { q: invSearch })); }
      catch { setInvResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [mode, invSearch]);

  const save = async () => {
    if (mode === "inventory") {
      if (!picked) return alert("Envanterden bir malzeme seçin.");
      const doc = await api.post("/catalog/materials/from-inventory", {
        material_id: picked.id,
        override_name: m.name || picked.name,
        override_price: m.price === "" ? null : Number(m.price),
        override_currency: m.currency,
        category: m.category || null,
      });
      onSaved?.(doc);
      onClose?.();
      return;
    }
    const body = {
      name: m.name, category: m.category || null,
      short_description: m.short_description, price: m.price === "" ? null : Number(m.price),
      currency: m.currency, note: m.note,
    };
    const doc = initial
      ? await api.put(`/catalog/materials/${initial.id}`, body)
      : await api.post("/catalog/materials", body);
    onSaved?.(doc);
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/55 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-[640px] max-w-full bg-white rounded-xl shadow-xl p-5" onClick={e => e.stopPropagation()}
           data-testid="material-editor">
        <div className="flex items-center justify-between mb-3">
          <div className="text-lg font-semibold">
            {initial ? "Malzemeyi Düzenle" : "Malzeme Ekle"}
          </div>
          <button onClick={onClose}><X size={16} /></button>
        </div>
        {!initial && (
          <div className="inline-flex gap-1 border rounded mb-3 p-0.5">
            <button onClick={() => setMode("manual")}
                    className={cx("px-3 py-1 text-xs rounded", mode === "manual" ? "bg-indigo-600 text-white" : "text-slate-600")}
                    data-testid="mode-manual">Manuel</button>
            <button onClick={() => setMode("inventory")}
                    className={cx("px-3 py-1 text-xs rounded", mode === "inventory" ? "bg-indigo-600 text-white" : "text-slate-600")}
                    data-testid="mode-inventory">Envanterden Seç</button>
          </div>
        )}
        {mode === "inventory" && (
          <div className="mb-3 border rounded p-2">
            <div className="flex items-center gap-2 mb-2">
              <Search size={14} className="text-slate-400" />
              <input value={invSearch} onChange={e => setInvSearch(e.target.value)}
                     placeholder="Envanterde ara (ad / kod)"
                     className="flex-1 px-2 py-1 text-sm border rounded" data-testid="inv-search" />
            </div>
            <div className="max-h-32 overflow-y-auto border rounded">
              {invResults.map(r => (
                <button key={r.id} onClick={() => { setPicked(r); setM(prev => ({ ...prev, name: r.name || prev.name })); }}
                        className={cx("w-full px-2 py-1 text-left text-xs hover:bg-slate-50 border-b last:border-b-0",
                          picked?.id === r.id && "bg-indigo-50")}
                        data-testid={`inv-pick-${r.id}`}>
                  <b>{r.name}</b> {r.code && <span className="text-slate-400">· {r.code}</span>}
                </button>
              ))}
              {invResults.length === 0 && (
                <div className="px-2 py-2 text-[11px] text-slate-400">Aramayla sonuç getirin.</div>
              )}
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-xs font-semibold text-slate-500">Malzeme Adı *</label>
            <input value={m.name} onChange={e => setM({ ...m, name: e.target.value })}
                   className="w-full px-2 py-1.5 border rounded text-sm" data-testid="mat-name" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Kategori</label>
            <select value={m.category || ""} onChange={e => setM({ ...m, category: e.target.value })}
                    className="w-full px-2 py-1.5 border rounded text-sm" data-testid="mat-category">
              <option value="">—</option>
              {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Fiyat</label>
            <div className="flex gap-1">
              <input type="number" step="0.01" value={m.price ?? ""}
                     onChange={e => setM({ ...m, price: e.target.value })}
                     className="flex-1 px-2 py-1.5 border rounded text-sm" data-testid="mat-price" />
              <select value={m.currency || "EUR"} onChange={e => setM({ ...m, currency: e.target.value })}
                      className="px-2 py-1.5 border rounded text-sm" data-testid="mat-currency">
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="col-span-2">
            <label className="text-xs font-semibold text-slate-500">Kısa Açıklama</label>
            <input value={m.short_description || ""} onChange={e => setM({ ...m, short_description: e.target.value })}
                   className="w-full px-2 py-1.5 border rounded text-sm" data-testid="mat-desc" />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-semibold text-slate-500">Not</label>
            <textarea value={m.note || ""} onChange={e => setM({ ...m, note: e.target.value })}
                      className="w-full px-2 py-1.5 border rounded text-sm" rows={2} data-testid="mat-note" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 text-sm border rounded">İptal</button>
          <button onClick={save} className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
                  data-testid="mat-save">
            {initial ? "Kaydet" : "Oluştur"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Product Editor
// ───────────────────────────────────────────────────────────────
function ProductEditor({ initial, customers, onClose, onSaved }) {
  const [p, setP] = useState(() => initial || {
    customer_name: "", customer_id: null, brand: "", name: "", description: "",
  });
  const save = async () => {
    if (!p.name?.trim()) return alert("Ürün adı zorunludur.");
    const body = {
      customer_name: p.customer_name || null,
      customer_id: p.customer_id || null,
      brand: p.brand || null,
      name: p.name,
      description: p.description || null,
    };
    const doc = initial
      ? await api.put(`/catalog/products/${initial.id}`, body)
      : await api.post("/catalog/products", body);
    onSaved?.(doc);
    onClose?.();
  };
  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/55 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-[640px] max-w-full bg-white rounded-xl shadow-xl p-5" onClick={e => e.stopPropagation()}
           data-testid="product-editor">
        <div className="flex items-center justify-between mb-3">
          <div className="text-lg font-semibold">{initial ? "Ürünü Düzenle" : "Ürün Ekle"}</div>
          <button onClick={onClose}><X size={16} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-500">Müşteri</label>
            <SmartSuggestInput value={p.customer_name || ""} items={customers}
              displayField="legal_name" idField="id"
              placeholder="Müşteri adı yazın veya listeden seçin..."
              onChange={({ value, selectedId }) =>
                setP({ ...p, customer_name: value, customer_id: selectedId })}
              data-testid="prod-customer" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Marka</label>
            <input value={p.brand || ""} onChange={e => setP({ ...p, brand: e.target.value })}
                   className="w-full px-2 py-1.5 border rounded text-sm" data-testid="prod-brand" />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-semibold text-slate-500">Ürün Adı *</label>
            <input value={p.name} onChange={e => setP({ ...p, name: e.target.value })}
                   className="w-full px-2 py-1.5 border rounded text-sm" data-testid="prod-name" />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-semibold text-slate-500">Kısa Ürün Tanıtımı / Özellikler</label>
            <textarea value={p.description || ""} onChange={e => setP({ ...p, description: e.target.value })}
                      className="w-full px-2 py-1.5 border rounded text-sm" rows={4}
                      placeholder="Serbest metin. Örn: Yoğun nem desteği sağlayan, hafif dokulu bakım kremidir."
                      data-testid="prod-desc" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 text-sm border rounded">İptal</button>
          <button onClick={save} className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
                  data-testid="prod-save">
            {initial ? "Kaydet" : "Oluştur"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Catalog Builder (seçili kayıtlarla PDF yarat)
// ───────────────────────────────────────────────────────────────
function CatalogBuilder({ catalogType, pickedItems, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [brand, setBrand] = useState("");
  const [showPrices, setShowPrices] = useState(true);
  const [items, setItems] = useState(() => pickedItems);
  const dnd = useDragReorder(() => items, setItems);

  const save = async () => {
    if (!name.trim()) return alert("Katalog adı zorunludur.");
    const payload = {
      name: name.trim(),
      catalog_type: catalogType,
      brand_name: catalogType === "product" ? (brand || null) : null,
      show_prices: catalogType === "material" ? !!showPrices : null,
      items: items.map((it, idx) => ({ id: it.id, order: idx })),
    };
    const doc = await api.post("/catalog/catalogs", payload);
    onSaved?.(doc);
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/55 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-[720px] max-w-full bg-white rounded-xl shadow-xl p-5 max-h-[90vh] overflow-y-auto"
           onClick={e => e.stopPropagation()} data-testid="catalog-builder">
        <div className="flex items-center justify-between mb-3">
          <div className="text-lg font-semibold">
            Katalog Oluştur · {catalogType === "material" ? "Malzeme" : "Ürün"}
          </div>
          <button onClick={onClose}><X size={16} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-500">Katalog Adı *</label>
            <input value={name} onChange={e => setName(e.target.value)}
                   className="w-full px-2 py-1.5 border rounded text-sm"
                   placeholder="Örn. 2026 Premium Ambalaj Kataloğu" data-testid="catalog-name" />
          </div>
          {catalogType === "product" && (
            <div>
              <label className="text-xs font-semibold text-slate-500">Marka Adı (opsiyonel)</label>
              <input value={brand} onChange={e => setBrand(e.target.value)}
                     className="w-full px-2 py-1.5 border rounded text-sm"
                     placeholder="Örn. Armure D'or" data-testid="catalog-brand" />
            </div>
          )}
          {catalogType === "material" && (
            <label className="inline-flex items-center gap-2 text-xs text-slate-700">
              <input type="checkbox" checked={showPrices} onChange={e => setShowPrices(e.target.checked)}
                     data-testid="catalog-show-prices" />
              <b>Fiyatları Göster</b>
            </label>
          )}
          <div>
            <div className="text-xs font-semibold text-slate-500 mb-1">
              Seçili {items.length} kayıt (sürükle-bırak ile sırala)
            </div>
            <div className="border rounded max-h-64 overflow-y-auto">
              {items.map((it, i) => (
                <div key={it.id} {...dnd.rowProps(i)}
                     className={cx(
                       "flex items-center gap-2 px-2 py-1.5 border-b last:border-b-0",
                       dnd.isDropTarget(i) && "bg-indigo-50",
                       dnd.isDragging(i) && "opacity-50",
                     )}
                     data-testid={`builder-row-${it.id}`}>
                  <button draggable {...dnd.handleProps(i)} className="cursor-move p-0.5 text-slate-400 hover:text-slate-700">
                    <GripVertical size={13} />
                  </button>
                  <div className="text-xs font-mono text-slate-400 w-6">{i + 1}</div>
                  <div className="flex-1 text-sm">
                    <b>{it.name}</b>
                    {it.brand && <span className="text-slate-400 text-xs"> · {it.brand}</span>}
                    {it.customer_name && <span className="text-slate-400 text-xs"> · {it.customer_name}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 text-sm border rounded">İptal</button>
          <button onClick={save} className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
                  data-testid="catalog-create-btn">
            Oluştur
          </button>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Catalog List tab
// ───────────────────────────────────────────────────────────────
function CatalogListTab({ catalogs, onReload }) {
  const [emailModal, setEmailModal] = useState(null);
  const [tgModal, setTgModal] = useState(null);
  const openPdf = async (cid) => {
    const blob = await api.getBlob(`/catalog/catalogs/${cid}/pdf`);
    openPdfPreview(blob, `Katalog_${cid.slice(0, 8)}.pdf`);
  };
  const doCopy = async (cid) => {
    await api.post(`/catalog/catalogs/${cid}/copy`, {});
    onReload?.();
  };
  const doDelete = async (cid) => {
    if (!window.confirm("Katalog silinsin mi? (Çöp kutusuna taşınır)")) return;
    await api.del(`/catalog/catalogs/${cid}`);
    onReload?.();
  };
  const sendTelegram = async () => {
    try {
      await api.post(`/catalog/catalogs/${tgModal.id}/send-telegram`, { chat_id: tgModal.chat_id });
      alert("Telegram gönderildi."); setTgModal(null);
    } catch (e) { alert(e?.response?.data?.detail || "Telegram hatası."); }
  };
  const sendEmail = async () => {
    try {
      await api.post(`/catalog/catalogs/${emailModal.id}/send-email`, { to_addr: emailModal.to_addr });
      alert("E-posta gönderildi."); setEmailModal(null);
    } catch (e) { alert(e?.response?.data?.detail || "E-posta hatası."); }
  };
  return (
    <div data-testid="tab-catalog-list">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs">
          <tr>
            <th className="text-left px-2 py-2">Katalog Adı</th>
            <th className="text-left px-2 py-2">Tip</th>
            <th className="text-left px-2 py-2">Marka</th>
            <th className="text-left px-2 py-2">Tarih</th>
            <th className="text-left px-2 py-2">Oluşturan</th>
            <th className="text-center px-2 py-2">İşlemler</th>
          </tr>
        </thead>
        <tbody>
          {catalogs.map(c => (
            <tr key={c.id} className="border-b hover:bg-slate-50" data-testid={`cat-row-${c.id}`}>
              <td className="px-2 py-2 font-medium">{c.name}</td>
              <td className="px-2 py-2">
                <span className={cx("text-[11px] px-2 py-0.5 rounded-full",
                  c.catalog_type === "material" ? "bg-amber-100 text-amber-700" : "bg-indigo-100 text-indigo-700")}>
                  {c.catalog_type === "material" ? "Malzeme" : "Ürün"}
                </span>
              </td>
              <td className="px-2 py-2 text-slate-600">{c.brand_name || "—"}</td>
              <td className="px-2 py-2 text-slate-500 text-xs">{fmtDate(c.created_at)}</td>
              <td className="px-2 py-2 text-slate-500 text-xs">{c.created_by_name}</td>
              <td className="px-2 py-2">
                <div className="flex items-center justify-center gap-1">
                  <button onClick={() => openPdf(c.id)} title="PDF Önizle"
                          className="p-1 text-slate-500 hover:text-indigo-600" data-testid={`cat-pdf-${c.id}`}>
                    <FileText size={15} />
                  </button>
                  <button onClick={() => setTgModal({ id: c.id, chat_id: "" })} title="Telegram"
                          className="p-1 text-slate-500 hover:text-sky-600" data-testid={`cat-tg-${c.id}`}>
                    <Send size={15} />
                  </button>
                  <button onClick={() => setEmailModal({ id: c.id, to_addr: "" })} title="E-posta"
                          className="p-1 text-slate-500 hover:text-emerald-600" data-testid={`cat-email-${c.id}`}>
                    <Mail size={15} />
                  </button>
                  <button onClick={() => doCopy(c.id)} title="Kopyala"
                          className="p-1 text-slate-500 hover:text-amber-600" data-testid={`cat-copy-${c.id}`}>
                    <Copy size={15} />
                  </button>
                  <button onClick={() => doDelete(c.id)} title="Sil"
                          className="p-1 text-slate-500 hover:text-red-600" data-testid={`cat-del-${c.id}`}>
                    <Trash2 size={15} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
          {catalogs.length === 0 && (
            <tr><td colSpan={6} className="text-center text-slate-400 py-8">Henüz hazırlanmış katalog yok.</td></tr>
          )}
        </tbody>
      </table>
      {tgModal && (
        <div className="fixed inset-0 z-[75] bg-slate-900/55 flex items-center justify-center" onClick={() => setTgModal(null)}>
          <div className="bg-white rounded-xl shadow-xl p-5 w-[400px]" onClick={e => e.stopPropagation()}>
            <div className="text-base font-semibold mb-2">Telegram'a Gönder</div>
            <input value={tgModal.chat_id} onChange={e => setTgModal({ ...tgModal, chat_id: e.target.value })}
                   placeholder="Chat ID" className="w-full px-2 py-1.5 border rounded text-sm" data-testid="tg-chat-id" />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setTgModal(null)} className="px-3 py-1.5 text-sm border rounded">İptal</button>
              <button onClick={sendTelegram} className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded"
                      data-testid="tg-send">Gönder</button>
            </div>
          </div>
        </div>
      )}
      {emailModal && (
        <div className="fixed inset-0 z-[75] bg-slate-900/55 flex items-center justify-center" onClick={() => setEmailModal(null)}>
          <div className="bg-white rounded-xl shadow-xl p-5 w-[400px]" onClick={e => e.stopPropagation()}>
            <div className="text-base font-semibold mb-2">E-posta ile Gönder</div>
            <input value={emailModal.to_addr} onChange={e => setEmailModal({ ...emailModal, to_addr: e.target.value })}
                   placeholder="alici@firma.com" className="w-full px-2 py-1.5 border rounded text-sm"
                   data-testid="email-to" />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setEmailModal(null)} className="px-3 py-1.5 text-sm border rounded">İptal</button>
              <button onClick={sendEmail} className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded"
                      data-testid="email-send">Gönder</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Material Catalog Tab
// ───────────────────────────────────────────────────────────────
function MaterialTab({ categories, onCatalogCreated }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [editor, setEditor] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [builder, setBuilder] = useState(null);
  const load = useCallback(async () => {
    setItems(await api.get("/catalog/materials", { q: q || undefined, category: cat || undefined }));
  }, [q, cat]);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSelected(s => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const openBuilder = () => {
    const picked = items.filter(x => selected.has(x.id));
    if (picked.length === 0) return alert("En az bir malzeme seçin.");
    setBuilder(picked);
  };

  return (
    <div data-testid="tab-materials">
      <div className="flex items-center gap-2 mb-3">
        <div className="relative">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Ara..."
                 className="pl-7 pr-2 py-1.5 text-sm border rounded w-56" data-testid="mat-search" />
        </div>
        <select value={cat} onChange={e => setCat(e.target.value)}
                className="px-2 py-1.5 text-sm border rounded" data-testid="mat-filter-cat">
          <option value="">Tüm kategoriler</option>
          {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
        <div className="flex-1" />
        <button onClick={() => setEditor({})}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
                data-testid="mat-add-btn">
          <Plus size={14} /> Malzeme Ekle
        </button>
        <button onClick={async () => {
                  try {
                    const r = await api.post("/catalog/sync/inventory", {});
                    alert(`Senkronizasyon tamamlandı · yeni: ${r.created} · güncellenen: ${r.updated} · gizlenen: ${r.hidden || 0}`);
                    load();
                  } catch (e) { alert(e?.response?.data?.detail || "Senkronizasyon hatası."); }
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700"
                data-testid="mat-sync-inv-btn" title="Ürünler (Faz 2) → Katalog · pasif ürünler gizlenir">
          <PlusCircle size={14} /> Ürünlerden Senkronize Et
        </button>
        <button onClick={openBuilder}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700"
                data-testid="mat-build-catalog">
          <Package size={14} /> Katalog Oluştur ({selected.size})
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {items.map(m => (
          <div key={m.id} className={cx(
            "border rounded-lg p-3 bg-white",
            selected.has(m.id) ? "border-amber-400 ring-2 ring-amber-100" : "border-slate-200",
          )} data-testid={`mat-card-${m.id}`}>
            <div className="flex items-start gap-3">
              <input type="checkbox" checked={selected.has(m.id)} onChange={() => toggle(m.id)}
                     className="mt-1" data-testid={`mat-select-${m.id}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-semibold text-sm truncate">{m.name}</div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => setEditor(m)} className="p-1 text-slate-500 hover:text-indigo-600" title="Düzenle">
                      <Edit3 size={13} />
                    </button>
                    <button onClick={async () => {
                      if (!window.confirm("Silinsin mi?")) return;
                      await api.del(`/catalog/materials/${m.id}`); load();
                    }} className="p-1 text-slate-500 hover:text-red-600" title="Sil">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {m.category || "—"}
                  {m.code && <span className="ml-1 font-mono text-[10px]" data-testid={`mat-code-${m.id}`}>{m.code}</span>}
                  {m.source?.kind === "product_auto" && (
                    <span className="ml-1 text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded">ürün kartı</span>
                  )}
                  {m.source?.kind === "inventory_snapshot" && (
                    <span className="ml-1 text-[10px] px-1.5 py-0.5 bg-slate-100 rounded">envanter snapshot</span>
                  )}
                </div>
                {m.short_description && <div className="text-xs text-slate-600 mt-1">{m.short_description}</div>}
                {m.price != null && (
                  <div className="text-xs text-indigo-700 font-semibold mt-1">
                    {Number(m.price).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} {m.currency}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-3">
              <PhotoManager kind="material" record={m} onChange={load} />
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="col-span-full text-center text-slate-400 py-8">Henüz malzeme yok.</div>
        )}
      </div>
      {editor && (
        <MaterialEditor initial={editor.id ? editor : null} categories={categories}
                        onClose={() => setEditor(null)} onSaved={load} />
      )}
      {builder && (
        <CatalogBuilder catalogType="material" pickedItems={builder}
                        onClose={() => setBuilder(null)}
                        onSaved={() => { setSelected(new Set()); onCatalogCreated?.(); }} />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Product Catalog Tab
// ───────────────────────────────────────────────────────────────
function ProductTab({ customers, onCatalogCreated }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cust, setCust] = useState("");
  const [brand, setBrand] = useState("");
  const [editor, setEditor] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [builder, setBuilder] = useState(null);
  const load = useCallback(async () => {
    setItems(await api.get("/catalog/products", {
      q: q || undefined, customer: cust || undefined, brand: brand || undefined,
    }));
  }, [q, cust, brand]);
  useEffect(() => { load(); }, [load]);
  const toggle = (id) => setSelected(s => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const openBuilder = () => {
    const picked = items.filter(x => selected.has(x.id));
    if (picked.length === 0) return alert("En az bir ürün seçin.");
    setBuilder(picked);
  };
  return (
    <div data-testid="tab-products">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Ara..."
                 className="pl-7 pr-2 py-1.5 text-sm border rounded w-56" data-testid="prod-search" />
        </div>
        <input value={cust} onChange={e => setCust(e.target.value)} placeholder="Müşteri"
               className="px-2 py-1.5 text-sm border rounded w-40" data-testid="prod-filter-cust" />
        <input value={brand} onChange={e => setBrand(e.target.value)} placeholder="Marka"
               className="px-2 py-1.5 text-sm border rounded w-40" data-testid="prod-filter-brand" />
        <div className="flex-1" />
        <button onClick={() => setEditor({})}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
                data-testid="prod-add-btn">
          <Plus size={14} /> Ürün Ekle
        </button>
        <button onClick={openBuilder}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700"
                data-testid="prod-build-catalog">
          <Package size={14} /> Katalog Oluştur ({selected.size})
        </button>
      </div>
      <div className="space-y-3">
        {items.map(p => (
          <div key={p.id} className={cx(
            "border rounded-lg p-3 bg-white",
            selected.has(p.id) ? "border-amber-400 ring-2 ring-amber-100" : "border-slate-200",
          )} data-testid={`prod-card-${p.id}`}>
            <div className="flex items-start gap-3">
              <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)}
                     className="mt-1" data-testid={`prod-select-${p.id}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-xs text-slate-500">
                      {p.customer_name && <>Müşteri: <b>{p.customer_name}</b></>}
                      {p.brand && <> &nbsp;·&nbsp; Marka: <b>{p.brand}</b></>}
                    </div>
                    <div className="font-semibold text-base mt-0.5">{p.name}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => setEditor(p)} className="p-1 text-slate-500 hover:text-indigo-600" title="Düzenle">
                      <Edit3 size={14} />
                    </button>
                    <button onClick={async () => {
                      if (!window.confirm("Ürün silinsin mi?")) return;
                      await api.del(`/catalog/products/${p.id}`); load();
                    }} className="p-1 text-slate-500 hover:text-red-600" title="Sil">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                {p.description && (
                  <div className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{p.description}</div>
                )}
              </div>
            </div>
            <div className="mt-3">
              <PhotoManager kind="product" record={p} onChange={load} />
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-center text-slate-400 py-8">Henüz ürün yok.</div>
        )}
      </div>
      {editor && (
        <ProductEditor initial={editor.id ? editor : null} customers={customers}
                       onClose={() => setEditor(null)} onSaved={load} />
      )}
      {builder && (
        <CatalogBuilder catalogType="product" pickedItems={builder}
                        onClose={() => setBuilder(null)}
                        onSaved={() => { setSelected(new Set()); onCatalogCreated?.(); }} />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Page root
// ───────────────────────────────────────────────────────────────
export default function CatalogPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("list"); // list | material | product
  const [catalogs, setCatalogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [customers, setCustomers] = useState([]);
  const loadCatalogs = useCallback(async () => {
    setCatalogs(await api.get("/catalog/catalogs"));
  }, []);
  const loadRefs = useCallback(async () => {
    const [cats, cust] = await Promise.all([
      api.get("/catalog/categories"),
      api.get("/customers").catch(() => []),
    ]);
    setCategories(cats); setCustomers(cust);
  }, []);
  useEffect(() => { loadCatalogs(); loadRefs(); }, [loadCatalogs, loadRefs]);

  return (
    <div className="space-y-4" data-testid="catalog-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Package size={22} className="text-amber-600" />
            Katalog Portalı
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Ambalaj/malzeme ve bitmiş ürün görsellerini organize edin, profesyonel katalog PDF'leri oluşturun.
            <span className="ml-2 text-[10px] text-slate-400">İzole modül · Diğer business kayıtlarına yazmaz.</span>
          </p>
        </div>
      </div>
      <div className="flex items-center gap-1 border-b border-slate-200">
        {[
          { id: "list",     label: "Katalog Listesi",   icon: FileText },
          { id: "material", label: "Malzeme Kataloğu",  icon: Boxes },
          { id: "product",  label: "Ürün Kataloğu",     icon: Package },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
                  className={cx(
                    "inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition",
                    tab === t.id
                      ? "border-amber-500 text-amber-700"
                      : "border-transparent text-slate-500 hover:text-slate-800",
                  )}
                  data-testid={`tab-btn-${t.id}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        {tab === "list"     && <CatalogListTab catalogs={catalogs} onReload={loadCatalogs} />}
        {tab === "material" && <MaterialTab categories={categories} onCatalogCreated={() => { loadCatalogs(); setTab("list"); }} />}
        {tab === "product"  && <ProductTab customers={customers} onCatalogCreated={() => { loadCatalogs(); setTab("list"); }} />}
      </div>
    </div>
  );
}
